<template>
	<div id="wrapper">
		<SidebarView :fixedAssets="true" :fixedAssetsVisible="true" :assetCategories="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Asset Categories</h6>
							<b-button class="akkurate-green-btn btn-size text-white" @click="openCreate">
								<font-awesome-icon class="me-1" :icon="['fas', 'plus']" />
								Add Category
							</b-button>
						</div>
						<div class="card-body">
							<div class="row g-2 mb-3 align-items-end">
								<div class="col-md-4">
									<label class="akkurate-dark small">Status</label>
									<b-form-select
										v-model="filters.is_active"
										:options="activeOptions"
										class="field-container"
										@change="loadList"
									/>
								</div>
								<div class="col-md-auto">
									<b-button class="akkurate-green-btn btn-size text-white" @click="loadList">
										<font-awesome-icon class="me-1" :icon="['fas', 'rotate']" />
										Refresh
									</b-button>
								</div>
							</div>

							<b-table
								striped
								bordered
								responsive
								:busy="busy"
								:items="rows"
								:fields="fields"
								show-empty
								class="table_text_size"
							>
								<template #table-busy>
									<div class="text-center text-danger my-2">
										<b-spinner class="align-middle" />
										<strong>Loading...</strong>
									</div>
								</template>
								<template #cell(is_active)="d">
									<span :class="d.item.is_active ? 'badge akkurate-green-bg' : 'badge akkurate-gray'">
										{{ d.item.is_active ? "Active" : "Inactive" }}
									</span>
								</template>
								<template #cell(actions)="d">
									<div class="d-flex flex-wrap gap-1">
										<b-button size="sm" variant="outline-secondary" @click="openEdit(d.item)">
											Edit
										</b-button>
									</div>
								</template>
							</b-table>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<b-modal v-model="showModal" size="xl" :title="editingId ? 'Edit category' : 'Create category'" @hidden="resetForm">
			<b-overlay :show="saving || loadingOne" rounded="sm">
				<div class="row g-2">
					<div class="col-md-6">
						<label class="akkurate-dark small">Name <span class="text-danger">*</span></label>
						<b-form-input v-model="form.name" class="field-container" />
						<errors v-if="errors.name">{{ errors.name[0] }}</errors>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Active</label>
						<b-form-checkbox v-model="form.is_active">Is active</b-form-checkbox>
						<errors v-if="errors.is_active">{{ errors.is_active[0] }}</errors>
					</div>

					<div class="col-12">
						<p class="small text-muted mb-1">
							Select COA subheads used for postings (required).
						</p>
					</div>

					<template v-for="f in coaFields">
						<div class="col-md-6" :key="f.key">
							<label class="akkurate-dark small">{{ f.label }} <span class="text-danger">*</span></label>
							<multiselect
								v-model="form[f.key]"
								:options="coaSubheads"
								label="name"
								track-by="id"
								placeholder="Select COA subhead"
								class="field-container"
								:custom-label="coaLabel"
							/>
							<errors v-if="errors[f.key]">{{ errors[f.key][0] }}</errors>
						</div>
					</template>

					<div v-if="formError" class="col-12">
						<div class="alert alert-danger py-2 small mb-0">{{ formError }}</div>
					</div>
				</div>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="showModal = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="saving || loadingOne" @click="save">
						<b-spinner v-if="saving" small class="me-1" />
						{{ editingId ? "Update" : "Create" }}
					</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "AssetCategories",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			busy: false,
			rows: [],
			fields: [
				{ key: "name", label: "Name" },
				{ key: "is_active", label: "Status" },
				{ key: "actions", label: "Actions" },
			],
			filters: { is_active: 1 },
			activeOptions: [
				{ value: 1, text: "Active" },
				{ value: 0, text: "Inactive" },
				{ value: "", text: "All" },
			],

			coaSubheads: [],
			coaFields: [
				{ key: "asset_cost_coa_subhead", label: "Asset cost COA" },
				{ key: "accumulated_depreciation_coa_subhead", label: "Accumulated depreciation COA" },
				{ key: "depreciation_expense_coa_subhead", label: "Depreciation expense COA" },
				{ key: "disposal_proceeds_coa_subhead", label: "Disposal proceeds COA" },
				{ key: "disposal_gain_loss_coa_subhead", label: "Disposal gain/loss COA" },
			],

			showModal: false,
			editingId: null,
			loadingOne: false,
			saving: false,
			form: null,
			errors: {},
			formError: "",
		};
	},
	created() {
		this.form = this.blankForm();
		this.loadCoa();
		this.loadList();
	},
	methods: {
		blankForm() {
			return {
				name: "",
				is_active: true,
				asset_cost_coa_subhead: null,
				accumulated_depreciation_coa_subhead: null,
				depreciation_expense_coa_subhead: null,
				disposal_proceeds_coa_subhead: null,
				disposal_gain_loss_coa_subhead: null,
			};
		},
		coaLabel({ code, name }) {
			return `${code || ""} — ${name || ""}`;
		},
		async loadCoa() {
			try {
				const res = await ApiService.get("/journals/dropdown");
				this.coaSubheads = res.data?.coa_subheads || [];
			} catch (e) {
				this.coaSubheads = [];
			}
		},
		async loadList() {
			this.busy = true;
			this.$Progress.start();
			try {
				const params = {
					per_page: 30,
				};
				if (this.filters.is_active !== "") {
					params.is_active = this.filters.is_active;
				}
				const res = await ApiService.get("/asset-categories", { params });
				const body = res.data;
				this.rows = body.data || body || [];
			} catch (e) {
				toast.fire({ icon: "error", title: e.response?.data?.message || "Failed to load categories" });
				this.rows = [];
			} finally {
				this.busy = false;
				this.$Progress.finish();
			}
		},
		openCreate() {
			this.resetForm();
			this.showModal = true;
		},
		async openEdit(row) {
			this.resetForm();
			this.editingId = row.id;
			this.showModal = true;
			this.loadingOne = true;
			try {
				const res = await ApiService.get(`/asset-categories/${row.id}`);
				const c = res.data?.data || res.data || {};
				this.form.name = c.name || "";
				this.form.is_active = !!c.is_active;
				// map ids to objects where possible
				const mapCoa = (id) => this.coaSubheads.find((x) => x.id === id) || (id ? { id, name: `#${id}` } : null);
				this.form.asset_cost_coa_subhead = mapCoa(c.asset_cost_coa_subhead_id);
				this.form.accumulated_depreciation_coa_subhead = mapCoa(c.accumulated_depreciation_coa_subhead_id);
				this.form.depreciation_expense_coa_subhead = mapCoa(c.depreciation_expense_coa_subhead_id);
				this.form.disposal_proceeds_coa_subhead = mapCoa(c.disposal_proceeds_coa_subhead_id);
				this.form.disposal_gain_loss_coa_subhead = mapCoa(c.disposal_gain_loss_coa_subhead_id);
			} catch (e) {
				this.formError = e.response?.data?.message || "Could not load category.";
			} finally {
				this.loadingOne = false;
			}
		},
		resetForm() {
			this.editingId = null;
			this.loadingOne = false;
			this.saving = false;
			this.errors = {};
			this.formError = "";
			this.form = this.blankForm();
		},
		payload() {
			return {
				name: this.form.name,
				is_active: !!this.form.is_active,
				asset_cost_coa_subhead_id: this.form.asset_cost_coa_subhead?.id,
				accumulated_depreciation_coa_subhead_id: this.form.accumulated_depreciation_coa_subhead?.id,
				depreciation_expense_coa_subhead_id: this.form.depreciation_expense_coa_subhead?.id,
				disposal_proceeds_coa_subhead_id: this.form.disposal_proceeds_coa_subhead?.id,
				disposal_gain_loss_coa_subhead_id: this.form.disposal_gain_loss_coa_subhead?.id,
			};
		},
		async save() {
			this.saving = true;
			this.errors = {};
			this.formError = "";
			this.$Progress.start();
			try {
				const payload = this.payload();
				if (this.editingId) {
					await ApiService.put(`/asset-categories/${this.editingId}`, payload);
					toast.fire({ icon: "success", title: "Category updated" });
				} else {
					await ApiService.post("/asset-categories", payload);
					toast.fire({ icon: "success", title: "Category created" });
				}
				this.showModal = false;
				this.loadList();
			} catch (e) {
				if (e.response?.status === 422 && e.response?.data?.errors) {
					this.errors = e.response.data.errors;
				} else {
					this.formError = e.response?.data?.message || "Save failed.";
				}
			} finally {
				this.saving = false;
				this.$Progress.finish();
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

